import { t } from '@lingui/core/macro';
import gql from 'graphql-tag';
import { useMemo } from 'react';

import { paidSearchGoalKPIs } from '@/api/settings/goal/paidSearchGoalKPIs';
import { fetcherSol } from '@/api/sol-fetcher';
import { getVariablesFromTableParamsNew } from '@/api/util/getVariablesFromTableParams';
import { type SolQueryParamsNew } from '@/components/InstantSearch';
import { useSolImmutableQuery, useSolInfiniteQuery, useSolQuery } from '@/hooks/useSolQuery';

import { attributionGoalKPIs } from './goal/attributionGoalKPIs';
import { campaignGoalKPIs } from './goal/campaignGoalKPIs';
import { channelGoalKPIs } from './goal/channelGoalKPIs';
import { pixelGoalKPIs } from './goal/pixelGoalKPIs';
import { vendorGoalKPIs } from './goal/vendorGoalKPIs';
import { webTrafficGoalKPIs } from './goal/webTrafficGoalKPIs';
import {
  type GoalCreateGQLResponse,
  type GoalCreateVariablesType,
  type GoalInputType,
  type GoalUpdateGQLResponse,
  type GoalUpdateType,
  type GoalUpdateVariablesType,
  type ImpactScoreWeightsGQLResponse,
  type ImpactScoreWeightsSetAllGQLResponse,
  type ImpactScoreWeightsSetAllVariables,
  type IntentProductCategoriesGQLResponse,
  type NotificationCreateGQLResponse,
  type NotificationCreateVariablesType,
  type NotificationDeleteGQLResponse,
  type NotificationInputType,
  type NotificationPageMeta,
  type NotificationSubscriptionGQLResponse,
  type NotificationUnsubscribeGQLResponse,
  type NotificationUpdateGQLResponse,
  type NotificationUpdateType,
  type NotificationUpdateVariablesType,
  type PageMetaNotificationNameType,
  type RegionsGQLResponse,
  type RevenueRangesGQLResponse,
  type SectorsGQLResponse,
  type SignalTypesGQLResponse,
} from './settingsType';

export const useImpactScoreWeights = () => {
  const { data, isLoading, error, mutate } = useSolQuery<ImpactScoreWeightsGQLResponse>({
    query: gql`
      query GetImpactScoreWeights {
        impactScoreWeights {
          get {
            customScoringModelEnabled
            weights {
              id
              metricName
              metricType
              weight
            }
          }
        }
      }
    `,
  });

  return {
    settings: data?.impactScoreWeights.get,
    isLoading,
    error,
    mutate,
  };
};

export const mutateImpactScoreWeightsSetAll = async (
  variables: ImpactScoreWeightsSetAllVariables,
) => {
  const response = await fetcherSol<
    ImpactScoreWeightsSetAllVariables,
    ImpactScoreWeightsSetAllGQLResponse
  >({
    query: gql`
      mutation SetAllImpactScoreWeights(
        $customScoringModelEnabled: Boolean!
        $weights: [ImpactScoreWeightInput!]!
      ) {
        impactScoreWeights {
          setAll(customScoringModelEnabled: $customScoringModelEnabled, weights: $weights) {
            success
          }
        }
      }
    `,
    variables,
  });

  return response.impactScoreWeights.setAll.success;
};

export const useSectors = ({ includeBlanks = false } = {}) => {
  const { data, isLoading, error } = useSolImmutableQuery<SectorsGQLResponse>({
    query: gql`
      query getSectors(
        $page: Pagination!
        $sort: [SortParamInput]!
        $filter: [FilterParamInput]!
        $queryId: String
      ) {
        sectors {
          get(page: $page, sort: $sort, filter: $filter, queryId: $queryId) {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    `,
    variables: {
      page: {
        limit: 50,
        offset: 0,
      },
      sort: {
        direction: 'asc',
        field: 'name',
      },
      filter: [],
    },
  });

  const sectorNodes = data?.sectors.get.edges.map((edge) => edge.node);
  const sectors =
    sectorNodes && includeBlanks
      ? [
          {
            id: '',
            name: t`(Blanks)`,
          },
          ...sectorNodes,
        ]
      : sectorNodes;

  return {
    sectors,
    isLoading,
    error,
  };
};

export const useRegions = ({ includeBlanks = false } = {}) => {
  const { data, isLoading, error } = useSolImmutableQuery<RegionsGQLResponse>({
    query: gql`
      query GetRegions(
        $page: Pagination!
        $sort: [SortParamInput]!
        $filter: [FilterParamInput]!
        $queryId: String
      ) {
        regions {
          get(page: $page, sort: $sort, filter: $filter, queryId: $queryId) {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    `,
    variables: {
      page: {
        limit: 50,
        offset: 0,
      },
      sort: {
        direction: 'asc',
        field: 'name',
        nullsFirst: includeBlanks,
      },
      filter: [],
    },
  });

  const regionNodes = data?.regions.get.edges
    .map((edge) => edge.node)
    .filter((region) => region.id !== 'regn_antarctica');

  const regions =
    regionNodes && includeBlanks
      ? [
          {
            id: '',
            name: t`(Blanks)`,
          },
          ...regionNodes,
        ]
      : regionNodes;

  return {
    regions,
    isLoading,
    error,
  };
};

export const useRevenueRanges = ({ includeBlanks = false } = {}) => {
  const { data, isLoading, error } = useSolImmutableQuery<RevenueRangesGQLResponse>({
    query: gql`
      query GetRevRanges(
        $page: Pagination!
        $sort: [SortParamInput]!
        $filter: [FilterParamInput]!
        $queryId: String
      ) {
        revRanges {
          get(page: $page, sort: $sort, filter: $filter, queryId: $queryId) {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    `,
    variables: {
      page: {
        limit: 50,
        offset: 0,
      },
      sort: {
        direction: 'asc',
        field: 'name',
        nullsFirst: includeBlanks,
      },
      filter: [],
    },
  });

  const revRangeNodes = data?.revRanges.get.edges.map((edge) => edge.node);

  const revRanges =
    revRangeNodes && includeBlanks
      ? [
          {
            id: '',
            name: t`(Blanks)`,
          },
          ...revRangeNodes,
        ]
      : revRangeNodes;

  return {
    revRanges,
    isLoading,
    error,
  };
};

export const useSignalTypes = () => {
  const { data, isLoading, error } = useSolImmutableQuery<SignalTypesGQLResponse>({
    query: gql`
      query GetSignalTypes {
        signalTypes {
          getAll {
            id
            name: displayName
          }
        }
      }
    `,
  });

  return {
    signalTypes: data?.signalTypes.getAll ?? [],
    isLoading,
    error,
  };
};

export const useIntentProductCategories = ({
  search = '',
  pageSize = 50,
}: {
  search?: string;
  pageSize?: number;
} = {}) => {
  const { data, isLoading, isFetchingNextPage, error, fetchNextPage } =
    useSolInfiniteQuery<IntentProductCategoriesGQLResponse>({
      queryKey: ['intentProductCategories', search],
      queryFn: ({ pageParam = 0, signal }) => {
        return {
          query: gql`
            query GetIntentProductCategories(
              $page: Pagination!
              $sort: [SortParamInput]!
              $filter: [FilterParamInput]!
            ) {
              productCategories {
                get(page: $page, sort: $sort, filter: $filter) {
                  edges {
                    node {
                      id
                      name
                    }
                  }
                  pageMeta {
                    hasNext
                  }
                }
              }
            }
          `,
          variables: {
            page: {
              offset: pageParam * pageSize,
              limit: pageSize,
            },
            sort: {
              direction: 'asc',
              field: 'name',
            },
            filter: search
              ? [
                  {
                    field: 'name',
                    operator: 'contains',
                    operand: search,
                  },
                ]
              : [],
          },
          signal,
        };
      },
      getNextPageParam: (lastPage, pages) => {
        return lastPage.productCategories.get.pageMeta.hasNext ? pages.length : undefined;
      },
    });

  const hasMore = data?.pages?.[data.pages.length - 1].productCategories.get.pageMeta.hasNext;

  const productCategories = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.pages.map((d) => d.productCategories.get.edges.map((edge) => edge.node)).flat();
  }, [data]);

  return {
    productCategories,
    hasMore,
    isLoading,
    isFetchingNextPage,
    error,
    fetchNextPage,
  };
};

export const useNotificationPageMeta = (): Record<
  PageMetaNotificationNameType,
  NotificationPageMeta
> => {
  const attributionKpis = attributionGoalKPIs();
  const campaignKpis = campaignGoalKPIs();
  const channelKpis = channelGoalKPIs();
  const paidSearchKpis = paidSearchGoalKPIs();
  const pixelKpis = pixelGoalKPIs();
  const vendorKpis = vendorGoalKPIs();
  const webTrafficKpis = webTrafficGoalKPIs();

  return {
    attribution: {
      kpiMetrics: attributionKpis.attribution,
    },
    campaign: {
      kpiMetrics: campaignKpis.campaign,
    },
    campaigns: {
      kpiMetrics: campaignKpis.campaigns,
    },
    channel: {
      kpiMetrics: channelKpis.channel,
    },
    channels: {
      kpiMetrics: channelKpis.channels,
    },
    company: {
      hasNoAudienceFilter: true,
    },
    'company-activity': {
      hasNoAudienceFilter: true,
    },
    dashboard: {
      hasNoAudienceFilter: true,
    },
    opportunity: {
      hasNoAudienceFilter: true,
    },
    opportunities: {
      hasNoAudienceFilter: true,
    },
    'paidsearch-adgroups': {
      kpiMetrics: paidSearchKpis['paidsearch-adgroups'],
    },
    pixel: {
      kpiMetrics: pixelKpis.pixel,
    },
    vendor: {
      kpiMetrics: vendorKpis.vendor,
    },
    vendors: {
      kpiMetrics: vendorKpis.vendors,
    },
    'web-traffic': {
      kpiMetrics: webTrafficKpis['web-traffic'],
    },
  };
};

export const useNotificationPageMetaByKey = (
  key?: PageMetaNotificationNameType,
): NotificationPageMeta => {
  const meta = useNotificationPageMeta();

  const pageMeta = key ? meta[key] : undefined;

  if (!pageMeta) {
    throw new Error(`Unknown page key: ${key}`);
  }

  return pageMeta;
};

export const useNotificationMetaKPILabel = () => {
  const meta = useNotificationPageMeta();
  const kpiLabels = useMemo(
    () =>
      Object.values(meta).reduce<Record<string, string>>((memo, pageMeta) => {
        pageMeta.kpiMetrics?.forEach((kpi) => {
          memo[kpi.value] = kpi.label;
        });
        return memo;
      }, {}),
    [meta],
  );

  return (kpiId: string) => {
    const kpiLabel = kpiLabels[kpiId];

    if (!kpiLabel) {
      throw new Error(`Unknown kpiId: ${kpiId}`);
    }

    return kpiLabel;
  };
};

export const useNotificationSubscriptionsList = (
  tableParams?: SolQueryParamsNew,
  subscriptionType?: 'all' | 'achieved',
) => {
  const variables = getVariablesFromTableParamsNew(tableParams);
  if (variables) {
    const filter = {
      field: 'achieved',
      operator: 'in',
      // null is for AI Digest type
      operand: subscriptionType === 'achieved' ? ['true'] : [null, 'false'],
    };
    variables.filter = [...(variables.filter ?? []), filter];
  }
  const { data, isLoading, error, mutate } = useSolQuery<NotificationSubscriptionGQLResponse>(
    {
      query:
        tableParams &&
        gql`
          query MyNotifications(
            $page: Pagination!
            $sort: [SortParamInput]!
            $filter: [FilterParamInput]!
          ) {
            notifications {
              mine(page: $page, sort: $sort, filter: $filter) {
                edges {
                  node {
                    id
                    type
                    name
                    achieved
                    additionalRecipients {
                      email
                      id
                      name
                    }
                    createdAt
                    createdBy {
                      email
                      id
                      name
                    }
                    goal
                    goalStartDate
                    repeatsWeeks
                    reportingWindowDays
                    metric
                    lastRunDate
                    lastGoalValue
                    kpiId
                    sunday
                    monday
                    tuesday
                    wednesday
                    thursday
                    friday
                    saturday
                    variableTemplate
                  }
                }
                totalEdges
              }
            }
          }
        `,
      variables,
    },
    {
      keepPreviousData: true,
    },
  );

  const subscriptions = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return data.notifications.mine.edges.map((edge) => {
      const { node } = edge;
      node.additionalRecipients = node.additionalRecipients ? node.additionalRecipients : [];

      node.type = node.type ?? 'recurring';

      if (node.type !== 'goal') {
        node.repeatsWeeks = `${node.repeatsWeeks}`;
        node.reportingWindowDays = `${node.reportingWindowDays}`;
        node.deliveryDays = [];

        if (node.sunday) {
          node.deliveryDays.push('0');
        }
        if (node.monday) {
          node.deliveryDays.push('1');
        }
        if (node.tuesday) {
          node.deliveryDays.push('2');
        }
        if (node.wednesday) {
          node.deliveryDays.push('3');
        }
        if (node.thursday) {
          node.deliveryDays.push('4');
        }
        if (node.friday) {
          node.deliveryDays.push('5');
        }
        if (node.saturday) {
          node.deliveryDays.push('6');
        }
      }

      return node;
    });
  }, [data]);

  return {
    subscriptions,
    totalResults: data?.notifications.mine.totalEdges,
    isLoading,
    error,
    mutate,
  };
};

export const mutateNotificationCreate = async (notification: NotificationInputType) => {
  const response = await fetcherSol<NotificationCreateVariablesType, NotificationCreateGQLResponse>(
    {
      query: gql`
        mutation CreateNotification($notification: CreateNotificationInput!) {
          notifications {
            createNotification(notification: $notification) {
              id
            }
          }
        }
      `,
      variables: {
        notification,
      },
    },
  );

  return response.notifications.createNotification.id;
};

export const mutateNotificationUpdate = async (notification: NotificationUpdateType) => {
  const response = await fetcherSol<NotificationUpdateVariablesType, NotificationUpdateGQLResponse>(
    {
      query: gql`
        mutation UpdateNotification($notification: UpdateNotificationInput!) {
          notifications {
            updateNotification(notification: $notification) {
              id
            }
          }
        }
      `,
      variables: {
        notification,
      },
    },
  );

  return response.notifications.updateNotification.id;
};

export const mutateGoalCreate = async (goal: GoalInputType) => {
  const response = await fetcherSol<GoalCreateVariablesType, GoalCreateGQLResponse>({
    query: gql`
      mutation CreateGoal($goal: CreateGoalInput!) {
        notifications {
          createGoal(goal: $goal) {
            id
          }
        }
      }
    `,
    variables: {
      goal,
    },
  });

  return response.notifications.createGoal.id;
};

export const mutateGoalUpdate = async (goal: GoalUpdateType) => {
  const response = await fetcherSol<GoalUpdateVariablesType, GoalUpdateGQLResponse>({
    query: gql`
      mutation UpdateGoal($goal: UpdateGoalInput!) {
        notifications {
          updateGoal(goal: $goal) {
            id
          }
        }
      }
    `,
    variables: {
      goal,
    },
  });

  return response.notifications.updateGoal.id;
};

export const mutateNotificationUnsubscribe = async (notificationId: string) => {
  const response = await fetcherSol<{ notificationId: string }, NotificationUnsubscribeGQLResponse>(
    {
      query: gql`
        mutation UnsubscribeNotification($notificationId: String!) {
          notifications {
            unsubscribe(id: $notificationId) {
              success
            }
          }
        }
      `,
      variables: {
        notificationId,
      },
    },
  );

  return response.notifications.unsubscribe.success;
};

export const mutateNotificationDelete = async (notificationId: string) => {
  const response = await fetcherSol<{ notificationId: string }, NotificationDeleteGQLResponse>({
    query: gql`
      mutation DeleteNotification($notificationId: String!) {
        notifications {
          delete(id: $notificationId) {
            success
          }
        }
      }
    `,
    variables: {
      notificationId,
    },
  });

  return response.notifications.delete.success;
};
